import ReactMarkdown, { type Options as ReactMarkdownOptions } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
// @ts-ignore -- katex ships an exports map; the stylesheet has no type declaration
import 'katex/dist/katex.min.css';

export interface IMarkdownRendererProps {
  content: string;
  className?: string;
}

const preprocessMath = (text: string) => {
  if (typeof text !== 'string') return '';
  return text
    .replace(/\\\[|\\\]/g, () => '$$')
    .replace(/\\\(|\\\)/g, () => '$');
};


const remarkPlugins = [remarkGfm, remarkMath];
const rehypePlugins: ReactMarkdownOptions['rehypePlugins'] = [[rehypeKatex, { output: 'html' }]];

const components = {
  a: ({ href, children }: any) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary text-break"
    >
      {children}
    </a>
  ),
  p: ({ children }: any) => <p className="mb-1">{children}</p>,
  ul: ({ children }: any) => <ul style={{ listStyleType: 'disc' }} className="mb-1 ps-4">{children}</ul>,
  ol: ({ children }: any) => <ol style={{ listStyleType: 'decimal' }} className="mb-1 ps-4">{children}</ol>,
  li: ({ children }: any) => <li className="lh-base">{children}</li>,
  strong: ({ children }: any) => <strong className="fw-semibold">{children}</strong>,
  h1: ({ children }: any) => <h1 className="fw-bold fs-6 mb-1 text-body">{children}</h1>,
  h2: ({ children }: any) => <h2 className="fw-bold mb-1 text-body">{children}</h2>,
  h3: ({ children }: any) => <h3 className="fw-semibold mb-1 text-body">{children}</h3>,
  h4: ({ children }: any) => <h3 className="fw-semibold mb-1 text-body">{children}</h3>,
  code: ({ children }: any) => <code className="bg-light rounded px-1 font-monospace">{children}</code>,
  pre: ({ children }: any) => <pre className="bg-light rounded p-2 overflow-auto font-monospace mb-1">{children}</pre>,
  blockquote: ({ children }: any) => <blockquote className="border-start border-3 ps-3 mb-1 text-body-secondary">{children}</blockquote>,
  table: ({ children }: any) => (
    <div className="table-responsive mb-1">
      <table className="table table-bordered table-sm mb-0" style={{ minWidth: '32rem', backgroundColor: 'transparent',
        '--bs-table-bg': 'transparent', } as React.CSSProperties}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: any) => <thead className="table-light">{children}</thead>,
  th: ({ children, style }: any) => (
    <th style={style} className="fw-semibold align-top">{children}</th>
  ),
  td: ({ children, style }: any) => (
    <td style={style} className="align-top">{children}</td>
  ),
};

export default function MarkdownRenderer({ content, className }: IMarkdownRendererProps) {
  return (
    <div className={`markdown-renderer text-break ${className || ''}`}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {preprocessMath(content)}
      </ReactMarkdown>
    </div>
  );
}
